import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Waves, Activity, Heart, Brain, Sparkles, Users, Target, Zap, AlertTriangle } from 'lucide-react';

interface QuickCheckInProps {
  onComplete: (data: QuickCheckInData) => void;
  onSwitchToDeep: () => void;
  initialData?: QuickCheckInData;
}

export interface QuickCheckInData {
  energy_level: number;
  body_feeling_score: number;
  emotion_score: number;
  mind_clarity: number;
  spiritual_connection: number;
  social_connection: number;
  purpose_alignment: number;
  stress_level: number;
}

export default function QuickCheckIn({ onComplete, onSwitchToDeep, initialData }: QuickCheckInProps) {
  const [data, setData] = useState<QuickCheckInData>(initialData || {
    energy_level: 5,
    body_feeling_score: 5,
    emotion_score: 5,
    mind_clarity: 5,
    spiritual_connection: 5,
    social_connection: 5,
    purpose_alignment: 5,
    stress_level: 5
  });

  const dimensions = [
    {
      key: 'stress_level' as keyof QuickCheckInData,
      label: 'Stress',
      icon: AlertTriangle,
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      low: 'Calm',
      high: 'Overwhelmed',
      inverse: true // Higher is worse
    },
    {
      key: 'energy_level' as keyof QuickCheckInData,
      label: 'Energy',
      icon: Waves,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      low: 'Drained',
      high: 'Vibrant',
      inverse: false
    },
    {
      key: 'body_feeling_score' as keyof QuickCheckInData,
      label: 'Body',
      icon: Activity,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      low: 'Tense',
      high: 'Relaxed',
      inverse: false
    },
    {
      key: 'emotion_score' as keyof QuickCheckInData,
      label: 'Emotion',
      icon: Heart,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      low: 'Struggling',
      high: 'Thriving',
      inverse: false
    },
    {
      key: 'mind_clarity' as keyof QuickCheckInData,
      label: 'Mind',
      icon: Brain,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      low: 'Foggy',
      high: 'Clear',
      inverse: false
    },
    {
      key: 'spiritual_connection' as keyof QuickCheckInData,
      label: 'Spirit',
      icon: Sparkles,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      low: 'Disconnected',
      high: 'Connected',
      inverse: false
    },
    {
      key: 'social_connection' as keyof QuickCheckInData,
      label: 'Social',
      icon: Users,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      low: 'Isolated',
      high: 'Supported',
      inverse: false
    },
    {
      key: 'purpose_alignment' as keyof QuickCheckInData,
      label: 'Purpose',
      icon: Target,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      low: 'Lost',
      high: 'Aligned',
      inverse: false
    }
  ];

  const handleSliderChange = (key: keyof QuickCheckInData, value: number[]) => {
    setData(prev => ({ ...prev, [key]: value[0] }));
  };

  const getScoreColor = (score: number, inverse: boolean = false) => {
    if (inverse) {
      // For inverse scores (like stress), lower is better
      if (score <= 3) return 'text-primary';
      if (score <= 6) return 'text-secondary';
      return 'text-destructive';
    }
    if (score >= 7) return 'text-primary';
    if (score >= 4) return 'text-secondary';
    return 'text-destructive';
  };

  // Calculate average - invert stress for the average calculation
  const calculateWellnessScore = () => {
    const stressContribution = 11 - data.stress_level; // Invert: 10 stress = 1 wellness, 1 stress = 10 wellness
    const otherValues = [
      data.energy_level, data.body_feeling_score, data.emotion_score,
      data.mind_clarity, data.spiritual_connection, data.social_connection, data.purpose_alignment
    ];
    const total = stressContribution + otherValues.reduce((sum, val) => sum + val, 0);
    return Math.round(total / 8);
  };
  
  const averageScore = calculateWellnessScore();

  return (
    <div className="space-y-6 px-2">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 mb-4">
          <div className="p-2 rounded-full bg-primary/10">
            <Zap className="w-6 h-6 text-primary animate-pulse" />
          </div>
          <h3 className="text-2xl font-bold text-primary">Quick Check-In</h3>
        </div>
        <p className="text-muted-foreground">
          Rate how you're feeling across all dimensions (takes ~60 seconds)
        </p>
      </div>

      <Card className="p-6">
        <div className="space-y-8">
          {dimensions.map((dim) => {
            const Icon = dim.icon;
            const score = data[dim.key];
            
            return (
              <div key={dim.key} className="space-y-3 p-3 rounded-lg bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${dim.bgColor}`}>
                      <Icon className={`w-5 h-5 ${dim.color}`} />
                    </div>
                    <Label className="text-base font-medium">{dim.label}</Label>
                  </div>
                  <span className={`text-2xl font-bold ${getScoreColor(score, dim.inverse)}`}>
                    {score}
                  </span>
                </div>
                
                <Slider
                  value={[score]}
                  onValueChange={(value) => handleSliderChange(dim.key, value)}
                  min={1}
                  max={10}
                  step={1}
                  className="w-full"
                />
                
                <div className="flex justify-between text-xs text-muted-foreground px-1">
                  <span>{dim.low}</span>
                  <span>{dim.high}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Average Score Display */}
        <div className={`mt-8 p-4 rounded-lg border-2 ${
          averageScore >= 7 ? 'border-primary/20 bg-primary/5' :
          averageScore >= 4 ? 'border-secondary/20 bg-secondary/5' :
          'border-destructive/20 bg-destructive/5'
        }`}>
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-1">Overall Wellness</p>
            <p className={`text-4xl font-bold ${getScoreColor(averageScore)}`}>
              {averageScore}/10
            </p>
          </div>
        </div>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={() => onComplete(data)}
          className="flex-1 min-w-0"
          size="lg"
        >
          <span className="truncate">Complete Quick Check-In</span>
        </Button>
        <Button
          onClick={onSwitchToDeep}
          variant="outline"
          className="flex-1 min-w-0"
          size="lg"
        >
          <span className="truncate">Switch to Deep Dive</span>
        </Button>
      </div>

      <p className="text-xs text-center text-muted-foreground px-4">
        💡 Want more insights? Try the Deep Dive mode for detailed reflection and personalised recommendations
      </p>
    </div>
  );
}
